import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { useNavigate, useRoutes } from "react-router-dom";
import useAccess from "./lib/hooks/use-access-manager";
import { useUser } from "./lib/hooks/use-user";
import ContactOrganisationsDetails from "./page-components/contacts/organisations/[id]/page";
import ContactOrganisationsCreate from "./page-components/contacts/organisations/create/page";
import ContactOrganisations from "./page-components/contacts/organisations/page";
import Contacts from "./page-components/contacts/page";
import ContactPersonsDetails from "./page-components/contacts/persons/[id]/page";
import ContactPersonsCreate from "./page-components/contacts/persons/create/page";
import ContactPersons from "./page-components/contacts/persons/page";
import ContactsRootComponent from "./page-components/contacts/root-component";
import DashboardOld from "./page-components/dashboard-old/page";
import {
  default as DashboardOldRootComponent,
  default as DashboardRootComponent,
} from "./page-components/dashboard-old/root-component";
import Dashboard from "./page-components/dashboard/page";
import DealsItem from "./page-components/deals/[id]/page";
import CreateItem from "./page-components/deals/create/page";
import Deals from "./page-components/deals/page";
import { default as DealsRootComponent } from "./page-components/deals/root-component";
import NoAccess from "./page-components/error/no-access";
import PageNotFound from "./page-components/error/page-not-found";
import Help from "./page-components/help/page";
import HelpRootComponent from "./page-components/help/root-component";
import CreateInvoice from "./page-components/invoices/create/page";
import DeleteInvoice from "./page-components/invoices/delete/page";
import Invoices from "./page-components/invoices/page";
import InvoicesRootComponent from "./page-components/invoices/root-component";
import PersonalSettings from "./page-components/personal/page";
import PersonalRootComponent from "./page-components/personal/root-component";
import PipelineDetails from "./page-components/pipelines/[id]/page";
import CreatePipeline from "./page-components/pipelines/create/page";
import PipelinesRootComponent from "./page-components/pipelines/root-component";
import RegisterInformationsParent from "./page-components/register/informations/page";
import Logout from "./page-components/register/logout/page";
import Register from "./page-components/register/page";
import ResetPassword from "./page-components/register/reset-password";
import SalesItem from "./page-components/sales/[id]/page";
import CreateSalesItem from "./page-components/sales/create/page";
import Sales from "./page-components/sales/page";
import SalesRootComponent from "./page-components/sales/root-component";
import NumbersSettings from "./page-components/settings/numbers/page";
import Settings from "./page-components/settings/page";
import ProductItem from "./page-components/settings/products/[id]/page";
import CreateProduct from "./page-components/settings/products/create/page";
import ProductsSettings from "./page-components/settings/products/page";
import SettingsRootComponent from "./page-components/settings/root-component";
import SalesRankItem from "./page-components/settings/sales-ranks/[id]/page";
import CreateSalesRank from "./page-components/settings/sales-ranks/create/page";
import SalesRankDetails from "./page-components/settings/sales-ranks/details/page";
import SalesRanks from "./page-components/settings/sales-ranks/page";
import UserItem from "./page-components/settings/users/[id]/page";
import CreateUser from "./page-components/settings/users/create/page";
import Users from "./page-components/settings/users/page";
import Webhooks from "./page-components/settings/webhooks/page";
import MailVerification from "./page-components/verification/mail-verification";
import HomeRedirect from "./ui-components/ui/home-redirect";
import Preloader from "./ui-components/ui/preloader";

const Routes = () => {
  const [element, setElement] = useState(<Preloader />);
  const [isProfileComplete, setProfileComplete] = useState(false);
  const { user: loggedInUser } = useUser();
  const { hasRouteAccess } = useAccess();

  const { isAuthenticated, user, isLoading } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) {
      setElement(<Preloader />);
      return;
    }
    if (!isAuthenticated) {
      return;
    }
    setProfileComplete(true);
  }, [isAuthenticated, isLoading, navigate, user]);

  const getElement = (route: string, element: JSX.Element, title: string) => {
    if (!loggedInUser) {
      return <Preloader />;
    }
    if (!hasRouteAccess(route)) {
      return <NoAccess title={title} />;
    }
    return element;
  };

  const routes = useRoutes([
    {
      path: "/",
      element: isProfileComplete
        ? getElement("/", <DashboardRootComponent children={<Dashboard />} />, "das Dashboard")
        : element,
    },
    {
      path: "/dashboard-old",
      element: isProfileComplete
        ? getElement("/dashboard-old", <DashboardOldRootComponent children={<DashboardOld />} />, "das Dashboard")
        : element,
    },
    {
      path: "/pipelines",
      element: isProfileComplete
        ? getElement("/pipelines", <PipelinesRootComponent children={<HomeRedirect />} />, "die Pipelines")
        : element,
    },
    {
      path: "/pipelines/:id",
      element: isProfileComplete
        ? getElement("/pipelines/:id", <PipelinesRootComponent children={<PipelineDetails />} />, "die Pipelines")
        : element,
    },
    {
      path: "/pipelines/create",
      element: isProfileComplete
        ? getElement("/pipelines/create", <PipelinesRootComponent children={<CreatePipeline />} />, "die Pipelines")
        : element,
    },
    {
      path: "/deals",
      element: isProfileComplete
        ? getElement("/deals", <DealsRootComponent children={<Deals />} />, "die Deals")
        : element,
    },
    {
      path: "/deals/:id",
      element: isProfileComplete
        ? getElement("/deals/:id", <DealsRootComponent children={<DealsItem />} />, "die Deals")
        : element,
    },
    {
      path: "/deals/create",
      element: isProfileComplete
        ? getElement("/deals/create", <DealsRootComponent children={<CreateItem />} />, "die Deals")
        : element,
    },
    {
      path: "/contacts",
      element: isProfileComplete
        ? getElement("/contacts", <ContactsRootComponent children={<Contacts />} />, "die Kontakte")
        : element,
    },
    {
      path: "/contacts/organisations",
      element: isProfileComplete
        ? getElement(
            "/contacts/organisations",
            <ContactsRootComponent children={<ContactOrganisations />} />,
            "die Kontakte"
          )
        : element,
    },
    {
      path: "/contacts/organisations/:id",
      element: isProfileComplete
        ? getElement(
            "/contacts/organisations/:id",
            <ContactsRootComponent children={<ContactOrganisationsDetails />} />,
            "die Kontakte"
          )
        : element,
    },
    {
      path: "/contacts/organisations/create",
      element: isProfileComplete
        ? getElement(
            "/contacts/organisations/create",
            <ContactsRootComponent children={<ContactOrganisationsCreate />} />,
            "die Kontakte"
          )
        : element,
    },
    {
      path: "/contacts/persons",
      element: isProfileComplete
        ? getElement("/contacts/persons", <ContactsRootComponent children={<ContactPersons />} />, "die Kontakte")
        : element,
    },
    {
      path: "/contacts/persons/:id",
      element: isProfileComplete
        ? getElement(
            "/contacts/persons/:id",
            <ContactsRootComponent children={<ContactPersonsDetails />} />,
            "die Kontakte"
          )
        : element,
    },
    {
      path: "/contacts/persons/create",
      element: isProfileComplete
        ? getElement(
            "/contacts/persons/create",
            <ContactsRootComponent children={<ContactPersonsCreate />} />,
            "die Kontakte"
          )
        : element,
    },
    {
      path: "/invoices",
      element: isProfileComplete
        ? getElement("/invoices", <InvoicesRootComponent children={<Invoices />} />, "die Rechnungen")
        : element,
    },
    {
      path: "/invoices/create",
      element: isProfileComplete
        ? getElement("/invoices/create", <InvoicesRootComponent children={<CreateInvoice />} />, "die Rechnungen")
        : element,
    },
    {
      path: "/invoices/delete",
      element: isProfileComplete
        ? getElement("/invoices/delete", <InvoicesRootComponent children={<DeleteInvoice />} />, "die Rechnungen")
        : element,
    },
    {
      path: "/sales",
      element: isProfileComplete
        ? getElement("/sales", <SalesRootComponent children={<Sales />} />, "die Sales")
        : element,
    },
    {
      path: "/sales/create",
      element: isProfileComplete
        ? getElement("/sales/create", <SalesRootComponent children={<CreateSalesItem />} />, "die Sales")
        : element,
    },
    {
      path: "/sales/:id",
      element: isProfileComplete
        ? getElement("/sales/:id", <SalesRootComponent children={<SalesItem />} />, "die Sales")
        : element,
    },
    {
      path: "/register/info",
      element: <RegisterInformationsParent />,
    },
    { path: "/settings", element: <SettingsRootComponent children={<Settings />} /> },
    { path: "/settings/webhooks", element: <SettingsRootComponent children={<Webhooks />} /> },
    { path: "/settings/products", element: <SettingsRootComponent children={<ProductsSettings />} /> },
    { path: "/settings/products/create", element: <SettingsRootComponent children={<CreateProduct />} /> },
    { path: "/settings/products/:id", element: <SettingsRootComponent children={<ProductItem />} /> },
    { path: "/settings/users", element: <SettingsRootComponent children={<Users />} /> },
    { path: "/settings/users/create", element: <SettingsRootComponent children={<CreateUser />} /> },
    { path: "/settings/users/:id", element: <SettingsRootComponent children={<UserItem />} /> },
    { path: "/settings/sales-ranks", element: <SettingsRootComponent children={<SalesRanks />} /> },
    { path: "/settings/sales-ranks/create", element: <SettingsRootComponent children={<CreateSalesRank />} /> },
    { path: "/settings/sales-ranks/:id", element: <SettingsRootComponent children={<SalesRankItem />} /> },
    { path: "/settings/sales-ranks/details", element: <SettingsRootComponent children={<SalesRankDetails />} /> },
    { path: "/settings/numbers", element: <SettingsRootComponent children={<NumbersSettings />} /> },
    { path: "/personal", element: <PersonalRootComponent children={<PersonalSettings />} /> },
    { path: "/help", element: <HelpRootComponent children={<Help />} /> },
    { path: "/logout", element: <Logout /> },
    { path: "/verification/mail", element: <MailVerification /> },
    { path: "/reset", element: <ResetPassword /> },
    { path: "/register", element: <Register /> },
    { path: "/*", element: <PageNotFound /> },
  ]);

  return routes;
};

export default Routes;
